import { IntercomScreen } from '@getstep/sdk/dist/store/intercom/Screens'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useEffect } from 'react'

import type { Values } from './common'
import { EnterCode } from './common'
import { SsoType } from '@app-types/sso'
import type { FormProps } from '@contentful-components/forms/props'
import { CustomFieldId, useField } from '@graphql/hooks/field'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { useHelpCenterScreen } from '@lib/hooks/useHelpCenterScreen'
import { useInterval } from '@lib/hooks/useInterval'
import { progressStore } from '@lib/store/ui/progress'
import { snackbarStore } from '@lib/store/ui/snackbar'
import { useAuthorizedStores, useSession } from '@lib/store/useStores'
import { OtpVerificationStatus } from '@lib/utils/login'
import { renderTemplate, TEXT } from '@lib/utils/text'
import { LINK, LocalLink } from '@shared/util/link'

/**
 * This form verifies OTP for given email.
 */
export const EnterEmailCode: FunctionComponent<FormProps<Values>> = ({
    next: nextFromProps,
    title,
    renderSubmit,
}) => {
    const router = useRouter()
    const { emailVerificationStore: store } = useSession()
    const { userStore } = useAuthorizedStores()
    const next =
        nextFromProps ||
        LINK.verifySSOResult(SsoType.Email, 'success').serialize(router)

    const { errorTexts } = useField(CustomFieldId.LoginEmailOtp)

    useHelpCenterScreen(IntercomScreen.SIGNUP_ENTER_EMAIL_CODE)

    const events = ['visibilitychange', 'focus'] as const
    const isVerified = () =>
        userStore.verifiedEmailAlias?.value === store?.valueToVerify

    const disposer = () =>
        events.forEach((e) => window.removeEventListener(e, listener))

    const listener = progressStore.withGlobalLoading(async () => {
        await userStore.refresh()

        if (isVerified()) {
            disposer()
            snackbarStore.show(TEXT.emailVerified, 'login.email.verified')
            router.push(next)
        }
    })

    useInterval(listener, {
        interval: 3000,
        maxRetries: 10,
        isEnabled: isVerified,
    }) // if everything else fails

    useEffect(() => {
        events.forEach((e) => window.addEventListener(e, listener))
        return disposer
    }, [])

    if (!store) {
        return null // or a loading state, or redirect to login
    }

    return (
        <EnterCode
            customFieldsetId={CustomFieldsetId.LoginEnterEmailCode}
            title={renderTemplate(title, { value: store?.maskedEmail })}
            store={store}
            next={next}
            renderSubmit={renderSubmit}
            onSubmit={async (otp, { setError }) => {
                const result = await store?.completeVerification(otp)

                switch (result?.status) {
                    case OtpVerificationStatus.NO_SESSION: {
                        router.push(LINK.login(next).serialize(router))
                        return
                    }
                    case OtpVerificationStatus.SUCCESS: {
                        router.push(
                            new LocalLink({ base: next }).serialize(router),
                        )
                        return
                    }
                    case OtpVerificationStatus.ERROR: {
                        setError('otp', {
                            message: result.error.userFriendlyDescription,
                        })
                        return
                    }
                    case OtpVerificationStatus.FAIL: {
                        setError('otp', {
                            message: errorTexts.validate,
                        })
                        return
                    }
                    default: {
                        setError('otp', {
                            message: errorTexts.connectionFailure,
                        })
                        return
                    }
                }
            }}
        />
    )
}
